import { formatarCentavos, formatarNumero } from '../utils';
import type { ModoRifa } from '../types';

interface CaixaRoxaProps {
  modo: ModoRifa;
  valorCentavos: number;
  porTalao: number;
  inscritos: number;
  valorInscricaoCentavos: number;
  metaCentavos: number;
  absorvoATaxa: boolean;
}

const LIMITE_NUMEROS = 200000;

function Linha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-sm text-primary-100">{rotulo}</span>
      <span className="whitespace-nowrap text-sm font-medium text-white">{valor}</span>
    </div>
  );
}

function SeloVerde({ children }: { children: string }) {
  return (
    <div className="flex items-start gap-2 rounded-lg bg-green-100 px-3 py-2.5">
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-600 text-white">
        <i className="ri-check-line text-sm" />
      </span>
      <p className="text-sm text-green-900">{children}</p>
    </div>
  );
}

function SeloAmbar({ children, extra }: { children: string; extra?: string }) {
  return (
    <div className="flex items-start gap-2 rounded-lg bg-amber-100 px-3 py-2.5">
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-500 text-white">
        <i className="ri-alert-line text-sm" />
      </span>
      <div className="text-sm">
        <p className="text-amber-900">{children}</p>
        {extra && <p className="mt-1 text-amber-800/90">{extra}</p>}
      </div>
    </div>
  );
}

export default function CaixaRoxa({
  modo,
  valorCentavos,
  porTalao,
  inscritos,
  valorInscricaoCentavos,
  metaCentavos,
  absorvoATaxa,
}: CaixaRoxaProps) {
  const valorZero = valorCentavos <= 0;

  // Uma única variável decide tudo na caixa roxa:
  const rendePorNumero = absorvoATaxa
    ? valorCentavos - Math.ceil(valorCentavos * 0.07)
    : valorCentavos;

  const talaoRende = porTalao * rendePorNumero;

  let titulo = '';
  let tituloApoio = '';
  let totalNumeros = 0;
  const linhas: { rotulo: string; valor: string }[] = [];

  if (modo === 'evento') {
    const numerosTotal = inscritos * porTalao;
    totalNumeros = numerosTotal;
    titulo = formatarCentavos(inscritos * porTalao * rendePorNumero);
    tituloApoio = 'se vender tudo';
    linhas.push(
      { rotulo: 'Participantes', valor: formatarNumero(inscritos) },
      { rotulo: 'Números no total', valor: formatarNumero(numerosTotal) },
      { rotulo: 'Cada talão rende', valor: formatarCentavos(talaoRende) },
    );
  } else {
    const numerosMeta = valorZero ? 0 : Math.ceil(metaCentavos / rendePorNumero);
    const taloes = porTalao <= 0 ? 0 : Math.ceil(numerosMeta / porTalao);
    totalNumeros = taloes * porTalao;
    titulo = formatarCentavos(metaCentavos);
    tituloApoio = 'meta da obra';
    linhas.push(
      { rotulo: 'Números para bater a meta', valor: formatarNumero(numerosMeta) },
      { rotulo: 'Talões a distribuir', valor: formatarNumero(taloes) },
      { rotulo: 'Cada talão traz', valor: formatarCentavos(talaoRende) },
    );
  }

  const muitosNumeros = totalNumeros > LIMITE_NUMEROS;

  const cobreInscricao = talaoRende >= valorInscricaoCentavos;
  const nNecessario =
    rendePorNumero > 0
      ? Math.ceil(valorInscricaoCentavos / rendePorNumero)
      : porTalao + 1;
  // "faltar pouco": o talão cobriria sem a taxa, mas não cobre absorvendo.
  const faltaPouco =
    absorvoATaxa &&
    !cobreInscricao &&
    porTalao * valorCentavos >= valorInscricaoCentavos;

  return (
    <div className="overflow-hidden rounded-lg bg-primary-600 text-white">
      <div className="p-5">
        <p className="text-sm font-medium text-primary-100">
          {modo === 'evento' ? 'Conta da ação entre amigos' : 'Conta da obra'}
        </p>
        <div className="mt-2">
          <p className="text-3xl font-bold leading-tight">{titulo}</p>
          <p className="mt-0.5 text-sm text-primary-100">{tituloApoio}</p>
        </div>

        <div className="mt-4 space-y-2.5 border-t border-primary-400/40 pt-4">
          {linhas.map((l) => (
            <Linha key={l.rotulo} rotulo={l.rotulo} valor={l.valor} />
          ))}
        </div>

        {muitosNumeros && (
          <div className="mt-4">
            <SeloAmbar>Números demais. Suba o valor de cada número.</SeloAmbar>
          </div>
        )}

        {modo === 'evento' && !valorZero && valorInscricaoCentavos > 0 && (
          <div className="mt-4">
            {cobreInscricao ? (
              <SeloVerde>Talão inteiro quita a inscrição.</SeloVerde>
            ) : (
              <SeloAmbar
                extra={
                  faltaPouco
                    ? `Dá para resolver colocando ${nNecessario} números por talão em vez de ${porTalao} — os números a mais cobrem a taxa.`
                    : undefined
                }
              >
                {`Falta ${formatarCentavos(
                  valorInscricaoCentavos - talaoRende,
                )} por pessoa. Para quitar, precisaria de ${nNecessario} números por talão.`}
              </SeloAmbar>
            )}
          </div>
        )}
      </div>
    </div>
  );
}